import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import ComputerIcon from "@mui/icons-material/Computer";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import BuildIcon from "@mui/icons-material/Build";

interface Project {
  title: string;
  subtitle: string;
  description: string;
  url?: string;
  inProgress?: boolean;
  icon: React.ReactNode;
  technologies: string[];
  gradient: string;
  accentColor: string;
}

const OurWork: React.FC = () => {
  const projects: Project[] = [
    {
      title: "Atlas",
      subtitle: "Data Management",
      description:
        "A 3D Infrastructure Digital Twin for any user-defined system or asset that can be customized to the user's needs.",
      url: "https://atlas-2-0-front.vercel.app",
      icon: (
        <img
          src="/atlas-logo-symbol-color.png"
          alt="Atlas"
          style={{ width: 64, height: 64, objectFit: "contain" }}
        />
      ),
      technologies: ["React", "Node.js", "Cloud Services", "AI/MCP"],
      gradient: "linear-gradient(135deg, #0d1b2a 0%, #0a3d62 60%, #1f6fb2 100%)",
      accentColor: "#42a5f5",
    },
    {
      title: "RJD Tech Solutions",
      subtitle: "Technology Consulting",
      description:
        "A comprehensive technology consulting platform designed to help businesses navigate the digital landscape. RJD Tech Solutions provides expert guidance on software development, IT infrastructure, and digital transformation strategies.",
      url: "https://rjd-techsolutions.com",
      icon: <ComputerIcon sx={{ fontSize: 48 }} />,
      technologies: ["React", "Node.js", "Cloud Services", "Consulting"],
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      accentColor: "#667eea",
    },
    {
      title: "RPM Process Control",
      subtitle: "Industrial Control Solutions",
      description:
        "A comprehensive platform for RPM Process Control, specialists in variable frequency drive (VFD) repackaging, pump-control panels, chemical storage tanks, and electrical control products. Serving industries including water/wastewater, hospitals, chemical process plants, and OEMs with 50+ years of expertise.",
      inProgress: false,
      icon: (
        <img
          src="/process-control-logo-only.png"
          alt="RPM Process Control"
          style={{ width: 64, height: 64, objectFit: "contain" }}
        />
      ),
      technologies: ["React", "TypeScript", "Material UI", "Industrial"],
      gradient: "linear-gradient(135deg, #F5A623 0%, #2D2D2D 100%)",
      accentColor: "#F5A623",
      url: "https://www.processcontrolllc.com/",
    },
    {
      title: "The Shelf Saga",
      subtitle: "Book Collection Management",
      description:
        "A beautifully designed book collection management system for bibliophiles. The Shelf Saga helps readers organize their libraries, track reading progress, discover new books, and connect with fellow book lovers.",
      url: "https://theshelfsaga.com",
      icon: <MenuBookIcon sx={{ fontSize: 48 }} />,
      technologies: ["React", "TypeScript", "Book APIs", "Social Features"],
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      accentColor: "#4facfe",
    },
    {
      title: "Homegrown",
      subtitle: "Farmers Market Platform",
      description:
        "A platform connecting local farmers and vendors with their communities. Homegrown enables farmers and stand workers to establish stalls at markets, manage their product listings, and help consumers discover fresh, local produce. Empowering small-scale agriculture over big business.",
      url: "https://homegrown-front.vercel.app/",
      icon: <StorefrontIcon sx={{ fontSize: 48 }} />,
      technologies: ["React", "TypeScript", "Material UI", "Geolocation"],
      gradient: "linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)",
      accentColor: "#56ab2f",
    },
    {
      title: "Home Health Charting",
      subtitle: "AI-Powered Healthcare Documentation",
      description:
        "An intelligent charting application for home health professionals. Leveraging AI integration via Model Context Protocol (MCP), this system assists caregivers with documentation, automates routine charting tasks, and provides intelligent suggestions to improve patient care records.",
      inProgress: true,
      icon: <LocalHospitalIcon sx={{ fontSize: 48 }} />,
      technologies: ["React", "TypeScript", "AI/MCP", "Healthcare APIs"],
      gradient: "linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)",
      accentColor: "#ee0979",
    },
  ];

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: "50vh",
          display: "flex",
          alignItems: "center",
          position: "relative",
          background: `
            radial-gradient(ellipse at 20% 40%, rgba(31, 111, 178, 0.12) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 60%, rgba(47, 62, 78, 0.15) 0%, transparent 50%),
            linear-gradient(180deg, #0a0f1a 0%, #111827 100%)
          `,
          pt: { xs: 8, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{ textAlign: "center", animation: "fadeInUp 0.8s ease-out" }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "#1f6fb2",
                fontWeight: 600,
                letterSpacing: 3,
                mb: 2,
                display: "block",
              }}
            >
              OUR WORK
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                fontWeight: 700,
                lineHeight: 1.1,
                mb: 3,
                color: "#f8fafc",
              }}
            >
              Projects We're
              <Box component="span" sx={{ color: "#1f6fb2" }}>
                {" "}
                Proud Of
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#94a3b8",
                fontSize: "1.1rem",
                maxWidth: 600,
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              Take a look at some of the digital experiences we've crafted for
              our clients. Each project represents our commitment to quality,
              innovation, and user-centered design.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Projects Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(180deg, #111827 0%, #0a0f1a 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {projects.map((project, index) => (
              <Grid item xs={12} key={index}>
                <Card
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(17, 24, 39, 0.8) 100%)",
                    border: "1px solid rgba(148, 163, 184, 0.08)",
                    overflow: "hidden",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: `0 20px 50px rgba(31, 111, 178, 0.15)`,
                      borderColor: "rgba(31, 111, 178, 0.2)",
                      "& .project-icon-wrapper": {
                        transform: "scale(1.05)",
                      },
                    },
                  }}
                >
                  <Grid container>
                    {/* Icon/Visual Side */}
                    <Grid item xs={12} md={4}>
                      <Box
                        className="project-icon-wrapper"
                        sx={{
                          height: { xs: 200, md: "100%" },
                          minHeight: { md: 350 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: project.gradient,
                          position: "relative",
                          transition: "transform 0.4s ease",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            background: "rgba(0, 0, 0, 0.1)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            color: "#fff",
                            position: "relative",
                            zIndex: 1,
                            textAlign: "center",
                          }}
                        >
                          {project.icon}
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              mt: 2,
                              textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                            }}
                          >
                            {project.title}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Content Side */}
                    <Grid item xs={12} md={8}>
                      <CardContent
                        sx={{
                          p: { xs: 3, md: 5 },
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="overline"
                          sx={{
                            color: project.accentColor,
                            fontWeight: 600,
                            letterSpacing: 2,
                            mb: 1,
                          }}
                        >
                          {project.subtitle}
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{
                            color: "#f8fafc",
                            fontWeight: 600,
                            mb: 2,
                          }}
                        >
                          {project.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "#94a3b8",
                            lineHeight: 1.8,
                            mb: 3,
                            flex: 1,
                          }}
                        >
                          {project.description}
                        </Typography>

                        {/* Technologies */}
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748b",
                              fontWeight: 600,
                              letterSpacing: 1,
                              mb: 1.5,
                              display: "block",
                            }}
                          >
                            TECHNOLOGIES
                          </Typography>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                          >
                            {project.technologies.map((tech, techIndex) => (
                              <Chip
                                key={techIndex}
                                label={tech}
                                size="small"
                                sx={{
                                  backgroundColor: "rgba(31, 111, 178, 0.1)",
                                  color: "#94a3b8",
                                  border: "1px solid rgba(31, 111, 178, 0.2)",
                                  fontWeight: 500,
                                  "&:hover": {
                                    backgroundColor: "rgba(31, 111, 178, 0.2)",
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* CTA */}
                        {project.inProgress ? (
                          <Button
                            variant="outlined"
                            startIcon={<BuildIcon />}
                            disabled
                            sx={{
                              alignSelf: "flex-start",
                              borderColor: "rgba(148, 163, 184, 0.3)",
                              color: "#94a3b8",
                              "&.Mui-disabled": {
                                borderColor: "rgba(148, 163, 184, 0.3)",
                                color: "#94a3b8",
                              },
                            }}
                          >
                            In Progress
                          </Button>
                        ) : project.url ? (
                          <Button
                            href={project.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            endIcon={<LaunchIcon />}
                            sx={{
                              alignSelf: "flex-start",
                              borderColor: project.accentColor,
                              color: project.accentColor,
                              "&:hover": {
                                borderColor: project.accentColor,
                                backgroundColor: `${project.accentColor}15`,
                              },
                            }}
                          >
                            Visit Website
                          </Button>
                        ) : null}
                      </CardContent>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 12 },
          background: `
            radial-gradient(ellipse at 50% 50%, rgba(31, 111, 178, 0.1) 0%, transparent 60%),
            linear-gradient(180deg, #0a0f1a 0%, #111827 100%)
          `,
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "1.75rem", md: "2.5rem" },
              fontWeight: 600,
              color: "#f8fafc",
              mb: 3,
            }}
          >
            Have a project in mind?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#94a3b8",
              mb: 4,
              maxWidth: 500,
              mx: "auto",
            }}
          >
            We'd love to hear about your ideas and explore how we can bring them
            to life.
          </Typography>
          <Button
            href="/contact"
            variant="contained"
            size="large"
            sx={{
              px: 5,
              py: 1.5,
              fontSize: "1.1rem",
            }}
          >
            Let's Talk
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default OurWork;
